import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, Mail, Phone } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success("Connexion réussie");
      navigate("/");
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/361d33f2-92ad-49b2-9d7c-c3ef86e3efe8/images/d02a93c74453f311a59d226160e9f71f03a52a25030e416c68d23ff6a408cee4.png')`,
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Logos alignés en haut - Guinée, METPS (centre), Simandou 2040 */}
          <div className="flex items-center justify-center gap-8 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/mvf8jz61_branding.png" 
              alt="Guinée"
              className="h-20 object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_igtsocial/artifacts/uyugnatb_photo_5782873435288571226_y.jpg" 
              alt="Ministère de l'Emploi, du Travail et de la Protection Sociale"
              className="h-[12.5rem] object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/jl6j0vv2_t%C3%A9l%C3%A9chargement.jpeg" 
              alt="Programme Simandou 2040"
              className="h-28 object-contain"
            />
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-4 text-center">
              <CardTitle className="font-heading text-2xl font-bold text-primary">Inspection Générale du Travail</CardTitle>
              <CardDescription className="text-slate-900">
                Plateforme de déclaration des bilans sociaux des entreprises
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Adresse email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-11"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <Link 
                    to="/mot-de-passe-oublie" 
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connexion...
                    </div>
                  ) : (
                    "Se connecter"
                  )}
                </Button>
              </form>

              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600 text-center">
                  <strong>Note:</strong> Seuls les cabinets habilités par l'IGT ont accès à cette plateforme.
                  Pour toute demande d'habilitation, veuillez contacter l'IGT.
                </p>
                <div className="flex items-center justify-center gap-6 mt-3 text-xs text-slate-700">
                  <a href="tel:+224613139393" className="flex items-center gap-1 hover:text-primary">
                    <Phone className="w-3 h-3" />
                    +224 613 13 93 93
                  </a>
                  <a href="mailto:info@igt.gov.gn" className="flex items-center gap-1 hover:text-primary">
                    <Mail className="w-3 h-3" />
                    info@igt.gov.gn
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-500 mt-6">
            © {new Date().getFullYear()} Inspection Générale du Travail - République de Guinée
          </p>
          <p className="text-center text-xs text-slate-400 mt-2">
            <a 
              href="https://www.okcgn.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Powered by <span className="font-semibold text-primary">ALO.sa</span>
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
